import { IntlShape } from 'react-intl'
import { NotificationOrToast } from '@web/walter-lib/notifications'
import { HttpError } from '@web/walter-lib/api'
import { PosthogEventCode } from '@walter/shared/constants/posthog'
import { logPosthogEvent } from '@walter/shared/utils/posthog'
import { lang } from './lang'
import { getErrorNotification, processErrorCode, processErrorResponse } from '~hooks/usePromise/utils'

/**
 * Todo use errors code instead of error messages when backend provides the codes
 */
export const ERRORS_TYPES = {
    incorrectLoginOrPassword: 'incorrect login or password',
    otpRequired: 'otp required',
    otpInvalid: 'otp is invalid',
    twoFaInvalid: 'invalid 2fa credentials',
    badPassword: 'bad password',
    incorrectReferrer: 'bad referrer id',
    userDisabled: 'user is disabled',
    totpInvalid: 'TOTP is invalid',
    invalidPhone: 'Invalid phone format',
    invalidInviteCode: 'Invalid phone format',
}

type ErrorKey = keyof typeof ERRORS_TYPES

/**
 * processRequestErrorData return type
 */
export type RequestErrorDataResponse = {
    [key in ErrorKey]?: string
}

export interface PosthogAuthErrorEvents {
    eventCode: PosthogEventCode
    networkEventCode: PosthogEventCode
}

/**
 * Get error message from phrase if any
 * Otherwise use default response
 */
// eslint-disable-next-line complexity
function getErrorMessage(errorKey: ErrorKey, defaultMessage: string, intl: IntlShape, signUpInviteOnly?: boolean) {
    let message: string

    switch (errorKey) {
        case 'incorrectLoginOrPassword':
            message = intl.formatMessage(lang.incorrectLoginOrPassword)
            break
        case 'otpInvalid':
            message = intl.formatMessage(lang.otpInvalid)
            break
        case 'otpRequired':
            message = intl.formatMessage(lang.otpRequired)
            break
        case 'incorrectReferrer':
            if (signUpInviteOnly) {
                message = intl.formatMessage(lang.incorrectInviteCode)
            } else {
                message = intl.formatMessage(lang.incorrectReferrer)
            }
            break
        case 'badPassword':
            message = intl.formatMessage(lang.badPassword)
            break
        case 'totpInvalid':
            message = intl.formatMessage(lang.totpInvalid)
            break
        case 'invalidPhone':
            message = intl.formatMessage(lang.invalidPhone)
            break
        case 'invalidInviteCode':
            message = intl.formatMessage(lang.invalidInviteCode)
            break
        case 'twoFaInvalid':
            message = intl.formatMessage(lang.invalidTwoFaCode)
            break
        default:
            message = defaultMessage
            break
    }

    return message
}

function getErrorKeyByCode(errorCode: number): ErrorKey | undefined {
    let errorCodeKey

    switch (errorCode) {
        case 2012:
            errorCodeKey = 'invalidInviteCode' as const
            break
    }

    return errorCodeKey
}

/**
 * Get request error type and message
 */
export function processRequestErrorData(
    error: unknown,
    intl: IntlShape,
    pushNotification?: (tx: NotificationOrToast) => void,
    signUpInviteOnly?: boolean
): RequestErrorDataResponse {
    if (!error) {
        return {}
    }

    const responseErrFmt = processErrorResponse(error)
    const errorCode = processErrorCode(error)

    const errorCodeKey = getErrorKeyByCode(errorCode)
    const [errorKeyByMessage] = (Object.entries(ERRORS_TYPES).find(
        ([, errorTypeMessage]) => errorTypeMessage.toLowerCase() === responseErrFmt.toLowerCase()
    ) ?? []) as [ErrorKey, string]

    const errorKey = errorCodeKey || errorKeyByMessage

    if (!errorKey) {
        pushNotification?.(getErrorNotification(error))
        return {}
    }

    const message = getErrorMessage(errorKey, responseErrFmt, intl, signUpInviteOnly)

    return { [errorKey]: message }
}

export const logPosthogAuthError = (error: unknown, { eventCode, networkEventCode }: PosthogAuthErrorEvents) => {
    if (error instanceof HttpError) {
        if (error.code === 'ERR_NETWORK') {
            logPosthogEvent(networkEventCode, {
                message: processErrorResponse(error) || error.message || error.code,
                screen: window.location.href,
            })
        } else {
            logPosthogEvent(eventCode, {
                message: processErrorResponse(error) || error.message,
                screen: window.location.href,
            })
        }
    }
}
